import * as React from "react"
import Pending, { PendingProps } from "../Pending"
import _ from "lodash"

interface WithPendingProps<T> {
  forwardedRef: React.Ref<T>
}

const withPending = <SyncProps extends object, AsyncProps extends object>(
  Component: React.ComponentType<SyncProps & AsyncProps>,
  fetchPropsAsync: (props: SyncProps) => Promise<AsyncProps>,
  pendingProps?: Partial<PendingProps<AsyncProps & SyncProps>>
) => {
  class WithPending extends React.Component<
    SyncProps & WithPendingProps<Pending<AsyncProps & SyncProps>>
  > {
    private pendingRef: React.RefObject<Pending<AsyncProps & SyncProps>>

    constructor(props: any) {
      super(props)
      this.pendingRef = React.createRef<Pending<AsyncProps & SyncProps>>()
    }
    public render() {
      const overrideProps = pendingProps || {}
      const ref = this.props.forwardedRef || this.pendingRef
      return (
        <Pending
          ref={ref}
          getData={this.getData}
          renderSuccess={this.renderSuccess}
          {...overrideProps}
        />
      )
    }

    public componentDidUpdate(prevProps: any) {
      const syncProps = this.getSyncProps()
      const { forwardedRef, ...prevSyncProps } = prevProps
      if (!_.isEqual(syncProps, prevSyncProps)) {
        if (forwardedRef && forwardedRef.current) {
          forwardedRef.current?.refresh?.call()
        } else {
          this.pendingRef?.current?.refresh()
        }
      }
    }

    private getSyncProps() {
      const { forwardedRef, ...syncProps } = this.props
      return syncProps
    }

    private getData = () =>
      fetchPropsAsync(this.props).then(asyncProps => ({
        ...this.props,
        ...asyncProps
      }))

    private renderSuccess = (asyncProps: AsyncProps) => (
      <Component {...this.props} {...asyncProps} />
    )
  }

  return React.forwardRef(
    (props: SyncProps, ref: React.Ref<Pending<AsyncProps & SyncProps>>) => (
      <WithPending {...props} forwardedRef={ref} />
    )
  )
}

export default withPending
